import { useState, useEffect, useId } from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import Carousel, { CarouselProps, ControlProps, ScrollMode } from 'nuka-carousel';
import beTwMerge from '@/utils/beTwMerge';

import { getComponentMargin, getAnchorId } from '@/utils/builderUtils';
import useMediaQuery from '@/hooks/useMediaQuery';
import useStore from '@/store/useStore';
import Button from '../../Button/Button';
import Title from '../../Title/Title';

const FiniteContainer = styled.div<IFiniteContainerProps>`
  .slider-container .slider-frame .slider-list {
    width: ${({ $itemsLength, $noText }) => `${$itemsLength * ($noText ? 304 : 229)}px !important`};

    @media (max-width: 767px) {
      width: ${({ $itemsLength }) => {
        const baseWidth = $itemsLength * 229;
        let adjustedWidth = baseWidth;

        if ($itemsLength >= 5) {
          adjustedWidth += 4 - 16 / $itemsLength;
        } else if ($itemsLength <= 3) {
          adjustedWidth -= 16;
        }

        return `${adjustedWidth}px !important`;
      }};
    }

    @media (min-width: 768px) {
      ${({ $itemsLength }) => $itemsLength < 4 && 'width: 100% !important;'};
    }

    @media (min-width: 1024px) {
      ${({ $itemsLength }) => $itemsLength < 4 && 'justify-content: center;'};
    }

    @media (min-width: 1342px) {
      ${({ $itemsLength }) =>
        $itemsLength < 5 &&
        `
      width: 100% !important;
      justify-content: center;
    `};
    }
  }

  .slider-container > .slider-frame > .slider-list > .slide {
    box-sizing: content-box;
    width: 221px !important;

    &:first-child {
      padding-left: 16px !important;
    }

    @media (min-width: 1024px) {
      width: ${({ $noText }) => ($noText ? '296px !important' : '160px !important')};
      &:first-child {
        padding-left: 0 !important;
      }
    }

    @media (min-width: 1314px) {
      width: ${({ $noText }) => ($noText ? '296px !important' : '221px !important')};
    }
  }

  .slideImgContainer img {
    height: inherit;
    width: inherit;
  }

  .slide.slide-visible .finite-carousel-skeleton {
    opacity: 0 !important;
  }
`;

const InfiniteContainer = styled.div<IInfiniteContainerProps>`
  .slider-container .slider-frame .slider-list {
    gap: 8px;
    width: ${({ $itemsLength }) => `calc(${$itemsLength * 3 * 229}px) !important`};
    @media (min-width: 768px) {
      width: ${({ $itemsLength }) => `calc(${$itemsLength * 3 * 229}px + 200vw) !important`};
    }
    @media (min-width: 1024px) {
      width: ${({ $itemsLength, $noText }) =>
        `calc(${$itemsLength * 3 * 229}px + ${$noText ? 500 : 200}vw) !important`};
    }
  }

  .slider-container > .slider-frame > .slider-list > .slide {
    width: 221px !important;

    @media (max-width: 767px) {
      transform: translateX(-50vw) !important;
    }

    @media (min-width: 1024px) {
      width: ${({ $noText }) => ($noText ? '296px !important' : '221px !important')};
    }
  }

  .slider-container {
    width: auto;
  }

  .slide.slide-visible .infinite-carousel-skeleton {
    opacity: 0 !important;
  }
`;

const Container = styled.div`
  .carouselSection {
    @media (min-width: 1024px) and (max-width: 1313px) {
      justify-content: space-evenly;
    }
  }
`;

interface IFiniteContainerProps {
  $noText: boolean;
  $itemsLength: number;
}

interface IInfiniteContainerProps {
  $noText: boolean;
  $itemsLength: number;
}

interface IOptionalButtonProps {
  text: string;
  url: string;
  newTab: boolean;
}

interface IGeneralCarouselItem {
  img: string;
  title: string;
  description: string;
  url?: string;
  alt: string;
}

interface ITitle {
  content: string;
  titleHTMLTag?: 'h1' | 'h2' | 'h3';
  titleSize?: 'extraLarge' | 'large' | 'medium' | 'small';
  titleWeight?: 'light' | 'normal' | 'medium' | 'bold' | 'black';
}

interface IGeneralCarouselProps {
  /**
   * The list of Carousel items.
   * - img*: The item image
   * - title*: Item title
   * - description: Item description
   * - url: Optional url to link to
   * - alt*: Alt text of the image for accessibility
   */
  items: IGeneralCarouselItem[];
  /**
   * Content title.
   * - content: The content of the header
   * - titleWeight: Optional header weight. Default to 'normal'
   */
  title: ITitle;
  /**
   * Optional content body text
   */
  description: string;
  /**
   * Enables wrap around mode where the carousel can infinitely navigate forwards and backwards
   */
  infinite: boolean;
  /**
   * Turn on to add margin to the bottom of the component
   */
  margin?: boolean;
  /**
   * Give to the component an anchor link id
   */
  anchorLinkID?: string;
  /**
   * Optional global header.
   * - content: The content of the header
   * - titleWeight: Optional header weight. Default to 'normal'
   */
  optionalGlobalHeader?: ITitle;
  /**
   * List of optional buttons.
   * - text*: Content text.
   * - url*: Url to link to.
   * - newTab*: Should the link be opened in a new tab?.
   */
  optionalButtons: IOptionalButtonProps[];
}

function FiniteCarouselItem({ item, key, noText, isActive, length }) {
  const [activeOnce, setActiveOnce] = useState(false);

  useEffect(() => {
    if (isActive) {
      setActiveOnce(true);
    }
  }, [isActive]);

  if (item?.url) {
    return (
      <a
        href={item.url}
        key={key + 1}
        className={beTwMerge(
          'tw-flex-col tw-gap-[8px] tw-inline-block hover:tw-no-underline hover:tw-text-be-dark-gray tw-cursor-pointer tw-w-[221px] lg:tw-w-[160px] min-[1314px]:tw-w-[221px]',
          noText && 'lg:tw-w-[296px] min-[1314px]:tw-w-[296px]',
          length < 4 && 'lg:tw-w-full'
        )}
      >
        <div
          className={beTwMerge(
            'slideImgContainer tw-flex tw-justify-center tw-relative tw-w-[221px] lg:tw-w-[160px] min-[1314px]:tw-w-[221px] tw-h-[221px] lg:tw-h-[160px] min-[1314px]:tw-h-[221px]',
            noText && 'lg:tw-w-[296px] min-[1314px]:tw-w-[296px] lg:tw-h-[296px] min-[1314px]:tw-h-[296px]',
            length < 4 && 'tw-w-full lg:tw-w-full'
          )}
        >
          <div
            className={beTwMerge(
              'finite-carousel-skeleton tw-absolute tw-inset-x-0 tw-inset-y-0 [transition:opacity_500ms_linear] tw-opacity-100 tw-bg-color-grey-50 tw-pointer-events-none tw-z-[1]',
              activeOnce && 'tw-opacity-0'
            )}
          />
          <Image
            src={item.img}
            alt={item.alt || ''}
            fill
            sizes="(max-width: 767px) 33vw, (max-width: 1023px) 25vw, 20vw"
            className="tw-aspect-square tw-object-cover"
          />
        </div>
        <div className="tw-mt-[8px]">
          <p className="tw-m-0 tw-mb-[8px] tw-no-underline">{item.title}</p>
          {!!item.description && <p className="tw-no-underline tw-font-[Avenir]">{item.description}</p>}
        </div>
      </a>
    );
  }
  return (
    <>
      <div
        className={beTwMerge(
          'slideImgContainer tw-flex tw-justify-center tw-relative tw-w-[221px] lg:tw-w-[160px] min-[1314px]:tw-w-[221px] tw-h-[221px] lg:tw-h-[160px] min-[1314px]:tw-h-[221px]',
          noText && 'lg:tw-w-[296px] min-[1314px]:tw-w-[296px] lg:tw-h-[296px] min-[1314px]:tw-h-[296px]',
          length < 4 && 'tw-w-full lg:tw-w-full'
        )}
      >
        <div
          className={beTwMerge(
            'finite-carousel-skeleton tw-absolute tw-inset-x-0 tw-inset-y-0 [transition:opacity_500ms_linear] tw-opacity-100 tw-bg-color-grey-50 tw-pointer-events-none tw-z-[1]',
            activeOnce && 'tw-opacity-0'
          )}
        />
        <Image
          src={item.img}
          alt={item.alt || ''}
          fill
          sizes="(max-width: 767px) 33vw, (max-width: 1023px) 25vw, 20vw"
          className="tw-aspect-square tw-object-cover"
        />
      </div>
      <div className="tw-mt-[8px]">
        <p className="tw-m-0 tw-mb-[8px] tw-no-underline">{item.title}</p>
        {!!item.description && <p className="tw-no-underline tw-font-[Avenir]">{item.description}</p>}
      </div>
    </>
  );
}

function InfiniteCarouselItem({ item, key, noText, isActive }) {
  const [activeOnce, setActiveOnce] = useState(false);

  useEffect(() => {
    if (isActive) {
      setActiveOnce(true);
    }
  }, [isActive]);

  if (item?.url) {
    return (
      <a
        href={item.url}
        key={key + 1}
        className={beTwMerge(
          'tw-inline-block tw-w-[221px] hover:tw-no-underline hover:tw-text-be-dark-gray tw-cursor-pointer tw-group',
          noText && 'lg:tw-w-[296px]'
        )}
      >
        <div className={beTwMerge('tw-relative tw-h-[221px] tw-w-full', noText && 'lg:tw-h-[296px]')}>
          <div
            className={beTwMerge(
              'infinite-carousel-skeleton tw-absolute tw-inset-x-0 tw-inset-y-0 [transition:opacity_500ms_linear] tw-opacity-100 tw-bg-color-grey-50 tw-pointer-events-none  tw-z-[1]',
              activeOnce && 'tw-opacity-0'
            )}
          />
          <Image
            src={item.img}
            alt={item.alt || ''}
            fill
            sizes="(max-width: 767px) 33vw, (max-width: 1023px) 25vw, 20vw"
            className="tw-aspect-square tw-object-cover"
          />
        </div>
        <div className="tw-mt-[8px]">
          <p className="tw-m-0 tw-mb-[8px] tw-no-underline">{item.title}</p>
          {!!item.description && <p className="tw-no-underline tw-font-[Avenir]">{item.description}</p>}
        </div>
      </a>
    );
  }

  return (
    <>
      <div className={beTwMerge('tw-relative tw-h-[221px] tw-w-full', noText && 'lg:tw-h-[296px]')}>
        <div
          className={beTwMerge(
            'infinite-carousel-skeleton tw-absolute tw-inset-x-0 tw-inset-y-0 [transition:opacity_500ms_linear] tw-opacity-100 tw-bg-color-grey-50 tw-pointer-events-none  tw-z-[1]',
            activeOnce && 'tw-opacity-0'
          )}
        />
        <Image
          src={item.img}
          alt={item.alt || ''}
          fill
          sizes="(max-width: 767px) 33vw, (max-width: 1023px) 25vw, 20vw"
          className="tw-aspect-square tw-object-cover"
        />
      </div>
      <div className="tw-mt-[8px]">
        <p className="tw-m-0 tw-mb-[8px] tw-no-underline">{item.title}</p>
        {!!item.description && <p className="tw-no-underline tw-font-[Avenir]">{item.description}</p>}
      </div>
    </>
  );
}

function FiniteCarousel({ items, noText, isPhoneScreen }) {
  const [current, setCurrent] = useState(0);
  const [windowSize, setWindowSize] = useState(0);
  const max1313 = useMediaQuery('(max-width: 1313px)');
  const max1023 = useMediaQuery('(max-width: 1023px)');

  const randomId = useId().replace(/:/g, '');

  const getSlidesToShow = () => {
    let fraction = 1;
    if (max1023) {
      fraction = (windowSize - 15) / 229 - 0.072;
    } else if (max1313) {
      fraction = noText ? (windowSize - 127) / 304 : 664 / 168;
    } else if (noText) {
      fraction = 4 + (Math.min(1328, windowSize - 127) - 1216) / 296;
    } else {
      fraction = 910 / 229;
    }
    return parseFloat(fraction.toFixed(2));
  };

  useEffect(() => {
    function handleResize() {
      setWindowSize(window?.innerWidth);
    }
    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const carouselConfig: CarouselProps = {
    cellAlign: 'left',
    cellSpacing: 8,
    disableEdgeSwiping: true,
    dragThreshold: 0.2,
    enableKeyboardControls: true,
    slidesToShow: getSlidesToShow(),
    slidesToScroll: isPhoneScreen ? 1 : 4,
    scrollMode: ScrollMode.remainder,
    wrapAround: false,
    renderBottomCenterControls: () => null,
    renderCenterLeftControls: (props: ControlProps) => (
      <div className="xl:tw-block tw-hidden lg:tw-block tw-relative tw-top-[-26px]">
        <button
          type="button"
          onClick={() => {
            props.previousSlide();
          }}
          disabled={props.currentSlide === 0}
          className={beTwMerge(
            'tw-w-16 tw-h-16 -tw-translate-x-1/2 tw-border-none tw-rounded-full tw-bg-white tw-drop-shadow-xl',
            props.currentSlide === 0 && 'tw-hidden'
          )}
          aria-label="Scroll to previous items"
        >
          <i className="iconfont iconfont-left" />
        </button>
      </div>
    ),
    renderCenterRightControls: (props: ControlProps) => (
      <div className="xl:tw-block tw-hidden lg:tw-block tw-relative tw-top-[-26px]">
        <button
          type="button"
          onClick={() => {
            props.nextSlide();
          }}
          disabled={props.currentSlide + props.slidesToShow >= props.slideCount}
          className={beTwMerge(
            'tw-w-16 tw-h-16 tw-translate-x-1/2 tw-border-none tw-rounded-full tw-bg-white tw-drop-shadow-xl',
            props.currentSlide + props.slidesToShow >= props.slideCount && 'tw-hidden'
          )}
          aria-label="Scroll to next items"
        >
          <i className="iconfont iconfont-right" />
        </button>
      </div>
    ),
    afterSlide: (index: number) => setCurrent(index),
    carouselId: randomId,
    withoutControls: items?.length < 4,
  };

  function isSlideActive(index: number): boolean {
    const offSet = getSlidesToShow() + 1;
    if (current + offSet <= items.length) {
      return index >= current && index <= current + offSet;
    }

    const difference = current + offSet - items.length;
    return index < difference || (index >= current && index <= current + offSet);
  }

  return (
    <FiniteContainer $itemsLength={items?.length} $noText={noText}>
      <Carousel {...carouselConfig}>
        {items?.map((item: IGeneralCarouselItem, key: number) => (
          <FiniteCarouselItem
            item={item}
            key={key}
            noText={noText}
            isActive={isSlideActive(key + 1)}
            length={items?.length}
          />
        ))}
      </Carousel>
    </FiniteContainer>
  );
}

function InfiniteCarousel({ items, noText, isPhoneScreen }) {
  const [current, setCurrent] = useState(0);
  const initialSlideIndex = 0;
  const min1280 = useMediaQuery('(min-width: 1280px)');
  // eslint-disable-next-line no-nested-ternary
  const slidesToShow = isPhoneScreen ? 2.5 : min1280 ? 5 : 4;

  const randomId = useId().replace(/:/g, '');

  const carouselConfig: CarouselProps = {
    cellAlign: 'right',
    dragThreshold: 0.2,
    enableKeyboardControls: true,
    renderBottomCenterControls: () => null,
    renderCenterLeftControls: (props: ControlProps) => (
      <div className="xl:tw-block tw-hidden lg:tw-block tw-relative tw-top-[-26px]">
        <button
          type="button"
          onClick={() => {
            props.previousSlide();
          }}
          className="tw-w-16 tw-h-16 -tw-translate-x-1/2 tw-border-none tw-rounded-full tw-bg-white tw-drop-shadow-xl"
          aria-label="Scroll to previous items"
        >
          <i className="iconfont iconfont-left" />
        </button>
      </div>
    ),
    renderCenterRightControls: (props: ControlProps) => (
      <div className="xl:tw-block tw-hidden lg:tw-block tw-relative tw-top-[-26px]">
        <button
          type="button"
          onClick={() => {
            props.nextSlide();
          }}
          className="tw-w-16 tw-h-16 tw-translate-x-1/2 tw-border-none tw-rounded-full tw-bg-white tw-drop-shadow-xl"
          aria-label="Scroll to next items"
        >
          <i className="iconfont iconfont-right" />
        </button>
      </div>
    ),
    slideIndex: initialSlideIndex,
    slidesToShow,
    slidesToScroll: isPhoneScreen ? 1 : 4,
    wrapAround: true,
    speed: 700,
    afterSlide: (index: number) => handleSlideChange(index),
    carouselId: randomId,
  };

  function isSlideActive(index: number): boolean {
    const offSet = slidesToShow + 1;
    if (index === items.length) {
      return true;
    }
    if (current + offSet <= items.length) {
      return index >= current && index <= current + offSet;
    }

    const difference = current + offSet - items.length;
    return index < difference || (index >= current && index <= current + offSet);
  }

  const handleSlideChange = (index: number): void => {
    setCurrent(index);
  };

  return (
    <InfiniteContainer $itemsLength={items?.length} $noText={noText}>
      <Carousel {...carouselConfig}>
        {items?.map((item: IGeneralCarouselItem, key: number) => (
          <InfiniteCarouselItem item={item} key={key} noText={noText} isActive={isSlideActive(key + 1)} />
        ))}
      </Carousel>
    </InfiniteContainer>
  );
}

export default function GeneralCarousel({
  items,
  title,
  infinite,
  margin = false,
  anchorLinkID,
  optionalGlobalHeader,
  description,
  optionalButtons,
}: IGeneralCarouselProps) {
  const isPhoneScreen = useStore(state => state.isMobile);

  const noText = !description && !title?.content;

  return (
    <div
      {...getAnchorId(anchorLinkID)}
      className={beTwMerge(
        'tw-text-base tw-text-be-dark-gray tw-w-full lg:tw-max-w-[1440px] tw-mx-auto lg:tw-px-[56px]',
        getComponentMargin(margin)
      )}
    >
      <Container>
        <div>
          {optionalGlobalHeader?.content && (
            <Title
              component={optionalGlobalHeader?.titleHTMLTag || 'h2'}
              size={optionalGlobalHeader?.titleSize}
              weight={optionalGlobalHeader?.titleWeight}
              alignment="center"
            >
              {optionalGlobalHeader?.content}
            </Title>
          )}
        </div>
        <section
          className={beTwMerge(
            'carouselSection tw-flex tw-flex-col lg:tw-items-center',
            !noText && 'lg:tw-flex-row lg:tw-gap-[48px] tw-gap-[24px]'
          )}
        >
          <div className="lg:tw-w-[300px] tw-px-[16px] lg:tw-px-0 tw-w-[85%]">
            {title?.content && (
              <Title
                alignment="left"
                weight={title?.titleWeight}
                component={title?.titleHTMLTag || 'h3'}
                size={title?.titleSize}
                styles={{ margin: '0px', marginBottom: '0px', paddingBottom: '8px' }}
              >
                {title?.content}
              </Title>
            )}
            {description && (
              <p className="tw-font-[Avenir] tablet:tw-text-be-sm desktop:tw-text-be-base lg:tw-w-3/5 min-[1314px]:tw-w-full">
                {description}
              </p>
            )}
            {optionalButtons?.length > 0 && (
              <div
                className={beTwMerge(
                  'tw-hidden lg:tw-block tw-mt-[16px] tw-min-w-[160px]',
                  !noText && 'tw-hidden',
                  noText && 'lg:tw-hidden'
                )}
              >
                {optionalButtons?.map((button: IOptionalButtonProps, index: number) => (
                  <Button
                    key={index + 1}
                    text={button.text}
                    url={button.url}
                    newTab={button.newTab}
                    border="black"
                    backgroundColor="transparent"
                    className="focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color]"
                  />
                ))}
              </div>
            )}
          </div>

          <div
            className={beTwMerge(
              'tw-w-full tw-overflow-hidden lg:tw-overflow-visible',
              !noText && 'min-[1314px]:tw-max-w-[910px] lg:tw-max-w-[664px] lg:tw-w-auto'
            )}
          >
            <div className="tw-w-full">
              {infinite ? (
                <InfiniteCarousel isPhoneScreen={isPhoneScreen} items={items} noText={noText} />
              ) : (
                <FiniteCarousel isPhoneScreen={isPhoneScreen} items={items} noText={noText} />
              )}
            </div>
          </div>

          {optionalButtons?.length > 0 && (
            <div
              className={beTwMerge(
                'lg:tw-hidden tw-flex tw-justify-center tw-mt-[16px] tw-min-w-[160px]',
                noText && 'lg:tw-inline-block'
              )}
            >
              {optionalButtons?.map((button: IOptionalButtonProps, index: number) => (
                <Button
                  key={index + 1}
                  text={button.text}
                  url={button.url}
                  newTab={button.newTab}
                  border="black"
                  backgroundColor="transparent"
                />
              ))}
            </div>
          )}
        </section>
      </Container>
    </div>
  );
}
